import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
} from '@mui/material';

import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../services/firebase';
import { BusinessType, ShopFormData } from '../types';
import MasterDataSelector from '../components/MasterDataSelector';
import { fetchBusinessCategories } from '../utils/businessCategories';
import { stripUndefined } from '../utils/firestoreSafe';

const steps = ['業種選択', 'マスタデータ', '店舗情報入力', '確認'];

const Register: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [provisionFailed, setProvisionFailed] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string>('');

  const [formData, setFormData] = useState<ShopFormData>({
    shopName: '',
    businessTypeCode: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    postalCode: '',
    prefecture: '',
    city: '',
    address: '',
    building: '',
    phoneNumber: '',
    email: '',
    websiteUrl: '',
    description: '',
    location: undefined,
    businessTypes: undefined,
  });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const loadBusinessTypes = async () => {
      try {
        // 実体は category_masters。'businessTypes' は存在しないコレクションで、
        // 読んでも 0 件になり、業種の欄が空のまま先へ進めなかった。
        const types = await fetchBusinessCategories();
        setBusinessTypes(types as unknown as BusinessType[]);
      } catch (err) {
        console.error('Error loading business types:', err);
        setError('業種の読み込みに失敗しました');
      }
    };
    loadBusinessTypes();
  }, []);

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      // Submit
      await handleSubmit();
    } else {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleChange = (field: keyof ShopFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // パスワード確認
      if (password !== confirmPassword) {
        throw new Error('パスワードが一致しません');
      }

      if (password.length < 6) {
        throw new Error('パスワードは6文字以上で設定してください');
      }

      // Firebase Authenticationでユーザーを作成
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.ownerEmail,
        password
      );
      const user = userCredential.user;

      // 店舗コード生成
      const generateShopCode = httpsCallable(functions, 'generateShopCode');
      const result = await generateShopCode({ businessTypeCode: formData.businessTypeCode });
      const { shopCode } = result.data as { shopCode: string };
      setGeneratedCode(shopCode);

      // 店舗登録
      const shopData = {
        shopCode,
        shopName: formData.shopName,
        businessTypeCode: formData.businessTypeCode,
        ownerId: user.uid, // Firebase AuthenticationのユーザーIDを設定
        ownerName: formData.ownerName,
        ownerEmail: formData.ownerEmail,
        ownerPhone: formData.ownerPhone,
        postalCode: formData.postalCode,
        prefecture: formData.prefecture,
        city: formData.city,
        address: formData.address,
        building: formData.building || '',
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        websiteUrl: formData.websiteUrl || '',
        description: formData.description || '',
        location: formData.location,
        businessTypes: formData.businessTypes,
        planId: 'free', // 初期プランはFree
        subscriptionStatus: 'trial',
        monthlyReservationCount: 0,
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // 任意項目（地区など）を選ばないと undefined が混ざり、
      // Firestore が書き込みごと弾く。落としてから書く。
      const shopRef = await addDoc(collection(db, 'shops'), stripUndefined(shopData));

      // 申し込んだ直後から使える状態にする。
      // ここを通さないと、卓も手当も支払い方法も無く、営業日の区切りも
      // 0時のままの店ができる。エラーは出ないので気づけない。
      // 業種の細目（businessSubcategories のID）を業態として渡す。
      const kind =
        formData.businessTypes?.subcategories?.[0] ||
        (formData.businessTypes?.categories?.[0] === 'nightlife'
          ? 'nightlife_snack'
          : 'restaurant_japanese');
      try {
        const provisionNewShop = httpsCallable(functions, 'provisionNewShop');
        await provisionNewShop({ shopId: shopRef.id, shopName: formData.shopName, kind });
      } catch (provisionErr) {
        // 初期設定に失敗しても登録自体は成立している。
        // ログインしたあと「開店前チェック」から入れ直せる。
        console.error('初期設定に失敗:', provisionErr);
        setProvisionFailed(true);
      }

      setSuccess(true);
      setActiveStep(steps.length);
    } catch (err: any) {
      console.error('Error registering shop:', err);
      setError(err.message || '店舗登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              業種を選択してください
            </Typography>
            <TextField
              select
              fullWidth
              label="業種"
              value={formData.businessTypeCode}
              onChange={handleChange('businessTypeCode')}
              margin="normal"
              required
            >
              {businessTypes.map(type => (
                <MenuItem key={type.id} value={type.code}>
                  {type.icon} {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              マスタデータを選択してください
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
              店舗の所在地と業種分類を選択してください。これらの情報はポータルサイトでの検索に使用されます。
            </Typography>
            <MasterDataSelector
              countryCode={formData.location?.countryCode}
              regionId={formData.location?.regionId}
              areaId={formData.location?.areaId}
              districtId={formData.location?.districtId}
              onLocationChange={(location) => setFormData({ ...formData, location })}
              categories={formData.businessTypes?.categories || []}
              subcategories={formData.businessTypes?.subcategories || []}
              detailedTypes={formData.businessTypes?.detailedTypes || []}
              onBusinessTypesChange={(businessTypes) => setFormData({ ...formData, businessTypes })}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              店舗情報を入力してください
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  label="店舗名"
                  value={formData.shopName}
                  onChange={handleChange('shopName')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="オーナー名"
                  value={formData.ownerName}
                  onChange={handleChange('ownerName')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="オーナーメールアドレス"
                  type="email"
                  value={formData.ownerEmail}
                  onChange={handleChange('ownerEmail')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="オーナー電話番号"
                  value={formData.ownerPhone}
                  onChange={handleChange('ownerPhone')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="パスワード"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  helperText="6文字以上で入力してください"
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="パスワード（確認）"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  error={confirmPassword !== '' && password !== confirmPassword}
                  helperText={confirmPassword !== '' && password !== confirmPassword ? 'パスワードが一致しません' : ''}
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="郵便番号"
                  value={formData.postalCode}
                  onChange={handleChange('postalCode')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="都道府県"
                  value={formData.prefecture}
                  onChange={handleChange('prefecture')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="市区町村"
                  value={formData.city}
                  onChange={handleChange('city')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="住所"
                  value={formData.address}
                  onChange={handleChange('address')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="建物名・部屋番号"
                  value={formData.building}
                  onChange={handleChange('building')}
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="店舗電話番号"
                  value={formData.phoneNumber}
                  onChange={handleChange('phoneNumber')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="店舗メールアドレス"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Webサイト"
                  value={formData.websiteUrl}
                  onChange={handleChange('websiteUrl')}
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="店舗説明"
                  value={formData.description}
                  onChange={handleChange('description')}
                />
              </Box>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              登録内容を確認してください
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
              <Box>
                <Typography><strong>業種:</strong> {businessTypes.find(t => t.code === formData.businessTypeCode)?.name}</Typography>
              </Box>
              <Box>
                <Typography><strong>店舗名:</strong> {formData.shopName}</Typography>
              </Box>
              <Box>
                <Typography><strong>オーナー名:</strong> {formData.ownerName}</Typography>
              </Box>
              <Box>
                <Typography><strong>メールアドレス:</strong> {formData.ownerEmail}</Typography>
              </Box>
              <Box>
                <Typography><strong>住所:</strong> 〒{formData.postalCode} {formData.prefecture}{formData.city}{formData.address} {formData.building}</Typography>
              </Box>
              <Box>
                <Typography><strong>店舗電話番号:</strong> {formData.phoneNumber}</Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            店舗登録が完了しました！
          </Alert>
          <Typography variant="h5" gutterBottom>
            あなたの店舗コード
          </Typography>
          <Typography variant="h3" color="primary" gutterBottom>
            {generatedCode}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            このコードは従業員がアプリからログインするときに使います。大切に保管してください。
          </Typography>

          {provisionFailed ? (
            <Alert severity="warning" sx={{ mt: 3 }}>
              初期設定が終わりませんでした。ログインしたあと「開店前チェック」から
              足りない設定を入れてください。
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                そのまま使える状態にしました
              </Typography>
              <Typography variant="body2" component="div">
                ・卓、商品カテゴリ、支払い方法を入れました<br />
                ・営業日の区切りを朝6時にしました（深夜の売上が翌日に分かれません）<br />
                {'\u30fb'}締日は月末、バックは伝票の切り方で総額が変わらない方式です<br />
                ・あとは商品と従業員を入れれば営業できます
              </Typography>
            </Alert>
          )}

          <Button
            variant="contained"
            sx={{ mt: 3 }}
            onClick={() => window.location.href = `${process.env.PUBLIC_URL || ''}/`}
          >
            ダッシュボードへ
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          店舗登録
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            戻る
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || (activeStep === 0 && !formData.businessTypeCode)}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              '登録'
            ) : (
              '次へ'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;
